import { useTranslation, type Translate } from '../../i18n/LocaleProvider';
import { LOCALES, LOCALE_OPTIONS, type Locale } from '../../i18n/locale';
import { cn } from '../../lib/cn';
import { PRESS } from '../../interaction';
import type { EscrowBuildLanguage } from './EscrowBuildHeader';
import { Menu, type MenuEntry, type MenuPlacement } from '../overlay/Menu';

/**
 * The offered languages as menu entries: a flag, and nothing else to look at.
 *
 * Each row keeps its language name as its accessible name while hiding it
 * visually (`hideLabel`), which is the whole reason the flag can stand alone —
 * a flag names a country rather than a language, and a screen reader given one
 * with no text has nothing to read.
 *
 * The names are the languages' own, never the interface's: the reader looking
 * for Portuguese must find "Português (Brasil)" on a page currently in English.
 *
 * `options` is the caller's list rather than the registry, because the header
 * renders these rows in two places and one of them must be able to offer none.
 */
export function languageMenuItems(
  options: readonly EscrowBuildLanguage[],
  locale: Locale,
  onLocaleChange: (next: Locale) => void,
  t: Translate,
): MenuEntry[] {
  return options.map((option) => ({
    id: `language-${option.value}`,
    /*
       Which language is on is said in the row's own name, not only in the tick.

       The tick is a state marker and is hidden from the accessibility tree (see
       `MenuRow`), so without this a reader opening the list would hear two
       languages and no indication of the one already selected — the answer is on
       the trigger, behind the menu they just opened.
    */
    label:
      option.value === locale
        ? t('ds.header.languageOptionCurrent', { language: option.label })
        : option.label,
    icon: <span aria-hidden="true" className="text-lg leading-none">{option.flag}</span>,
    hideLabel: true,
    // The tick is the only thing telling a reader which language is on, once the
    // rows are flags — so it is state, not decoration, and stays.
    shortcut: option.value === locale ? '✓' : undefined,
    onSelect: () => onLocaleChange(option.value),
  }));
}

/*
   The panel is narrowed to its content.

   `Menu`'s own `min-w-56` is right for a list of words and wrong for a list of
   two flags: it left the surface 224px wide around 20px of glyph. `min-w-0`
   neutralises the floor and `w-max` lets the panel size to the rows — both
   survive the `cn` merge for that same reason, tailwind-merge resolving each
   against the base class it contradicts.
*/
const PANEL = 'min-w-0 w-max';

/** The trigger's own shape, before a caller narrows it — a 44px control. */
const TRIGGER = cn(
  'relative inline-grid h-11 w-11 shrink-0 place-items-center rounded-xs border border-line bg-surface text-heading',
  'transition-colors duration-[var(--dur-fast)] hover:border-primary hover:bg-surface-muted',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);

export interface LanguageSelectProps {
  locale: Locale;
  onLocaleChange: (next: Locale) => void;
  /**
   * Merged over the trigger's own classes, so a header with its own control
   * shape passes that shape rather than a second copy of this one living here.
   */
  className?: string;
  placement?: MenuPlacement;
}

/**
 * The interface-language picker: a flag, and the menu that changes it.
 *
 * One component rather than the same markup in each place a language can be
 * changed — the platform header, the investor portal's bar, the sign-in stage —
 * because the three have to agree on the thing that is easy to get wrong: which
 * element carries the accessible name. The flag is decorative on every one of
 * them and the name is always in words.
 */
export function LanguageSelect({
  locale,
  onLocaleChange,
  className,
  placement = 'bottom-end',
}: LanguageSelectProps) {
  const { t } = useTranslation();
  const selected = LOCALE_OPTIONS[locale];

  return (
    <Menu
      label={t('ds.header.language')}
      placement={placement}
      items={languageMenuItems(LOCALES, locale, onLocaleChange, t)}
      className={PANEL}
      trigger={
        <button
          type="button"
          className={cn(TRIGGER, className)}
          aria-label={t('ds.header.languageNamed', { language: selected.label })}
        >
          <span aria-hidden="true" className="text-xl leading-none">{selected.flag}</span>
        </button>
      }
    />
  );
}
